import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  addGallery,
  deleteGallery,
  getAllGalleries,
  updateGallery,
} from "../../../../../services/gallerieServices";

import {
  addPhoto,
  deletePhoto,
  uploadImage,
} from "../../../../../services/photoServices";

import {
  buildAlbumsStats,
  filterAndSortAlbums,
  isDemoUser,
  normalizeAlbumPayload,
  normalizePhotoPayload,
  safeJsonParse,
} from "../utils/albumsPhotosUtils";

const INITIAL_FILTERS = {
  query: "",
  status: "all",
  sortOrder: "created_desc",
};

export default function useAlbumsPhotos() {
  const [albums, setAlbums] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photoSaving, setPhotoSaving] = useState(false);

  const [drawerMode, setDrawerMode] = useState(null);
  const [selectedAlbum, setSelectedAlbum] = useState(null);

  const [deleteCandidateId, setDeleteCandidateId] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);

  const setFilter = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const loadUserMode = useCallback(() => {
    const user =
      safeJsonParse(localStorage.getItem("user"), null) ||
      safeJsonParse(localStorage.getItem("userInfo"), null);

    setIsDemoMode(isDemoUser(user));
  }, []);

  const refreshGalleries = useCallback(async () => {
    setLoading(true);

    try {
      const data = await getAllGalleries();
      const safeData = Array.isArray(data) ? data : [];

      setAlbums(safeData);

      return safeData;
    } catch (error) {
      console.error("Erreur lors de la récupération des albums :", error);
      toast.error("Erreur lors de la récupération des albums.");

      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserMode();
    refreshGalleries();
  }, [loadUserMode, refreshGalleries]);

  const guardDemoMode = useCallback(() => {
    if (!isDemoMode) return false;

    toast.info("Fonction désactivée en mode démonstration.");
    return true;
  }, [isDemoMode]);

  const filteredAlbums = useMemo(
    () => filterAndSortAlbums(albums, filters),
    [albums, filters]
  );

  const stats = useMemo(
    () => buildAlbumsStats(albums, filteredAlbums),
    [albums, filteredAlbums]
  );

  const openCreateDrawer = useCallback(() => {
    if (guardDemoMode()) return;

    setSelectedAlbum(null);
    setDrawerMode("create");
  }, [guardDemoMode]);

  const openEditDrawer = useCallback(
    (album) => {
      if (guardDemoMode()) return;

      setSelectedAlbum(album);
      setDrawerMode("edit");
    },
    [guardDemoMode]
  );

  const openDetailsDrawer = useCallback((album) => {
    setSelectedAlbum(album);
    setDrawerMode("details");
  }, []);

  const openPhotosDrawer = useCallback((album) => {
    setSelectedAlbum(album);
    setDrawerMode("photos");
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerMode(null);
    setSelectedAlbum(null);
  }, []);

  const createAlbum = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = normalizeAlbumPayload(formData);

        await addGallery(payload);

        toast.success("Album créé avec succès.");
        closeDrawer();
        await refreshGalleries();
      } catch (error) {
        console.error("Erreur lors de la création de l'album :", error);
        toast.error("Erreur lors de la création de l'album.");
      } finally {
        setSaving(false);
      }
    },
    [closeDrawer, guardDemoMode, refreshGalleries]
  );

  const saveAlbum = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = normalizeAlbumPayload(formData);

        await updateGallery(payload);

        toast.success("Album modifié avec succès.");
        closeDrawer();
        await refreshGalleries();
      } catch (error) {
        console.error("Erreur lors de la modification de l'album :", error);
        toast.error("Erreur lors de la modification de l'album.");
      } finally {
        setSaving(false);
      }
    },
    [closeDrawer, guardDemoMode, refreshGalleries]
  );

  const askDelete = useCallback(
    (albumId) => {
      if (guardDemoMode()) return;
      setDeleteCandidateId(albumId);
    },
    [guardDemoMode]
  );

  const cancelDelete = useCallback(() => {
    setDeleteCandidateId(null);
  }, []);

  const confirmDelete = useCallback(
    async (albumId) => {
      if (guardDemoMode()) return;

      try {
        await deleteGallery(albumId);

        toast.success("Album supprimé avec succès.");
        setDeleteCandidateId(null);
        await refreshGalleries();
      } catch (error) {
        console.error("Erreur lors de la suppression de l'album :", error);
        toast.error("Erreur lors de la suppression de l'album.");
      }
    },
    [guardDemoMode, refreshGalleries]
  );

  const uploadPhotoImage = useCallback(async (imageFile) => {
    if (!imageFile) return null;

    const formData = new FormData();
    formData.append("image", imageFile);
    formData.append("category", "photos");

    const uploadResponse = await uploadImage(formData);

    return (
      uploadResponse?.filePath ||
      uploadResponse?.imageUrl ||
      uploadResponse?.url ||
      null
    );
  }, []);

  const syncSelectedAlbum = useCallback(
    async (albumId) => {
      const updatedAlbums = await refreshGalleries();
      const updatedAlbum = updatedAlbums.find(
        (album) => Number(album.id) === Number(albumId)
      );

      if (updatedAlbum) {
        setSelectedAlbum(updatedAlbum);
      }

      return updatedAlbum;
    },
    [refreshGalleries]
  );

  const addPhotoToAlbum = useCallback(
    async ({ albumId, photo_url, description, imageFile }) => {
      if (guardDemoMode()) return;

      if (!photo_url && !imageFile) {
        toast.error("Veuillez ajouter une photo ou renseigner une URL.");
        return;
      }

      setPhotoSaving(true);

      try {
        let finalPhotoUrl = photo_url;

        if (imageFile) {
          const uploadedUrl = await uploadPhotoImage(imageFile);

          if (uploadedUrl) {
            finalPhotoUrl = uploadedUrl;
          }
        }

        const payload = normalizePhotoPayload({
          gallerie_id: albumId,
          photo_url: finalPhotoUrl,
          description,
          is_active: true,
        });

        const createdPhoto = await addPhoto(payload);

        setSelectedAlbum((prev) => {
          if (!prev || Number(prev.id) !== Number(albumId)) return prev;

          return {
            ...prev,
            photos: [...(prev.photos || []), createdPhoto || payload],
          };
        });

        toast.success("Photo ajoutée avec succès.");
        await syncSelectedAlbum(albumId);
      } catch (error) {
        console.error("Erreur lors de l'ajout de la photo :", error);
        toast.error("Erreur lors de l'ajout de la photo.");
      } finally {
        setPhotoSaving(false);
      }
    },
    [guardDemoMode, syncSelectedAlbum, uploadPhotoImage]
  );

  const deletePhotoFromAlbum = useCallback(
    async ({ albumId, photoId }) => {
      if (guardDemoMode()) return;

      setPhotoSaving(true);

      try {
        await deletePhoto(photoId);

        setSelectedAlbum((prev) => {
          if (!prev || Number(prev.id) !== Number(albumId)) return prev;

          return {
            ...prev,
            photos: (prev.photos || []).filter(
              (photo) => Number(photo.id) !== Number(photoId)
            ),
          };
        });

        toast.success("Photo supprimée avec succès.");
        await syncSelectedAlbum(albumId);
      } catch (error) {
        console.error("Erreur lors de la suppression de la photo :", error);
        toast.error("Erreur lors de la suppression de la photo.");
      } finally {
        setPhotoSaving(false);
      }
    },
    [guardDemoMode, syncSelectedAlbum]
  );

  return {
    loading,
    saving,
    photoSaving,
    filters,
    setFilter,
    albums,
    filteredAlbums,
    selectedAlbum,
    drawerMode,
    deleteCandidateId,
    stats,
    actions: {
      refreshGalleries,
      openCreateDrawer,
      openEditDrawer,
      openDetailsDrawer,
      openPhotosDrawer,
      closeDrawer,
      createAlbum,
      updateAlbum: saveAlbum,
      askDelete,
      cancelDelete,
      confirmDelete,
      addPhotoToAlbum,
      deletePhotoFromAlbum,
    },
  };
}